/**
 * MessageList — scrollable message log. Respects user prefs:
 *   - autoscrollBottom: stick to bottom when new messages arrive
 *   - showTimestamps:   hide the `[HH:MM AM]` prefix on each message
 *
 * Detects @mentions of the current user (case-insensitive) and tags
 * matching messages with `.msg-mention` so they're visually highlighted.
 */
import { useEffect, useRef } from 'react';
import { Message } from './Message.js';
import type { ChatMessage } from '../lib/types.js';
import { usePrefs } from '../hooks/usePrefs.js';

interface Props {
    messages: ChatMessage[];
    meName?: string;
    /** Active room/PM tab key — changing this forces scroll-to-bottom so
     *  the joiner sees the latest chat on every room switch / first join. */
    threadKey?: string;
}

function isMention(m: ChatMessage, meName?: string): boolean {
    if (!meName) return false;
    if (m.from?.name === meName) return false;          // your own messages don't count
    if (m.kind !== 'say' && m.kind !== 'pm' && m.kind !== 'generic_mess') return false;
    const needle = meName.toLowerCase();
    const hay = (m.body ?? '').toLowerCase();
    // word-ish boundary so "synth" doesn't match inside "polysynth"
    return new RegExp(`(^|[^a-z0-9_])@?${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z0-9_]|$)`, 'i').test(hay);
}

export function MessageList({ messages, meName, threadKey }: Props) {
    const [prefs] = usePrefs();
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const lastThreadKeyRef = useRef<string | undefined>(undefined);
    const lastLenRef = useRef<number>(0);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const threadChanged    = threadKey !== lastThreadKeyRef.current;
        const becameNonEmpty   = lastLenRef.current === 0 && messages.length > 0;
        // History load arrives in one big batch — detect a length jump too,
        // not just empty→non-empty, in case a stray live frame snuck in first.
        const bigJump          = messages.length - lastLenRef.current >= 5;

        if (threadChanged || becameNonEmpty || bigJump) {
            lastThreadKeyRef.current = threadKey;
            lastLenRef.current = messages.length;
            // Defer to next frame so freshly-rendered messages are measured.
            // Use TWO rAFs because images/text rows can finalise their height
            // a tick after layout, and otherwise we'd scroll to a stale value.
            requestAnimationFrame(() => {
                el.scrollTop = el.scrollHeight;
                requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
            });
            return;
        }

        if (prefs.autoscrollBottom) {
            const stickToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
            if (stickToBottom) el.scrollTop = el.scrollHeight;
        }
        lastLenRef.current = messages.length;
    }, [messages.length, prefs.autoscrollBottom, threadKey]);

    // Compute day-separator boundaries: inject a separator above any
    // message whose date (yyyy-mm-dd in local time) differs from the
    // previous message's. Saves a separator for every load.
    function dayKey(ts: number): string {
        const d = new Date(ts * 1000);
        return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    }
    function dayLabel(ts: number): string {
        const d = new Date(ts * 1000);
        return d.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
    }

    return (
        <div className={`msg-list ${prefs.showTimestamps ? '' : 'msg-list-no-ts'}`} ref={scrollRef}>
            {messages.length === 0
                ? <div className="msg msg-system"><span className="msg-text msg-muted">(no messages yet — say hi)</span></div>
                : messages.map((m, i) => {
                    const prev = i > 0 ? messages[i - 1] : null;
                    const showSep = !prev || (prev.t && m.t && dayKey(prev.t) !== dayKey(m.t));
                    return (
                        <div key={m.localId} className="msg-row">
                            {showSep && (
                                <div className="msg-day-sep">
                                    <span>{dayLabel(m.t)}</span>
                                </div>
                            )}
                            <Message m={m} mention={isMention(m, meName)} />
                        </div>
                    );
                })}
        </div>
    );
}
